import React, { useState, useCallback, useMemo, useEffect } from 'react';
import api from '../services/api';
import { useApiQuery, useApiMutation } from '../hooks/useApiQuery';
import { useSocket } from '../context/SocketContext';
import useToast from '../hooks/useToast';

const ScannerDashboard = () => {
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [customId, setCustomId] = useState('');
  const socket = useSocket();
  const { showSuccess, showError } = useToast();

  // ✅ React Query: Fetch devices
  const {
    data: devices = [],
    isLoading: devicesLoading,
    refetch: refetchDevices,
  } = useApiQuery(
    ['devices'],
    () => api.get('/devices').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => showError('Failed to load devices'),
    }
  );

  // ✅ React Query: Fetch recent scans
  const {
    data: recentScans = [],
    isLoading: scansLoading,
    refetch: refetchScans,
  } = useApiQuery(
    ['recent-scans'],
    () => api.get('/sessions?limit=10').then(res => res.data),
    {
      staleTime: 10 * 1000, // 10 seconds
      onError: () => console.error('Could not load recent scans'),
    }
  );

  // ✅ Auto-select first device when data loads
  useEffect(() => {
    if (devices.length > 0 && !selectedDeviceId) {
      setSelectedDeviceId(devices[0]._id);
    }
  }, [devices, selectedDeviceId]);

  // ✅ Memoize selected device
  const selectedDevice = useMemo(() => {
    return devices.find(d => d._id === selectedDeviceId);
  }, [devices, selectedDeviceId]);

  // ✅ Socket listener for real-time scan updates
  useEffect(() => {
    if (!socket) return;
    
    const handleNewScan = (data) => {
      if (data.session) {
        refetchScans();
        showSuccess(`${data.session.status} – ${data.session.name} via ${data.deviceName}`);
      }
    };
    
    socket.on('sessionScanned', handleNewScan);
    return () => socket.off('sessionScanned', handleNewScan);
  }, [socket, refetchScans, showSuccess]);

  // ✅ React Query: Scan mutation
  const scanMutation = useApiMutation(
    (data) => api.post('/sessions/scan', data).then(res => res.data),
    {
      onSuccess: (data, variables) => {
        showSuccess(`${variables.type.toUpperCase()} recorded`);
        setCustomId('');
        refetchScans();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Scan failed');
      },
    }
  );

  // ✅ Handle scan
  const handleScan = useCallback((type) => {
    if (!selectedDeviceId) {
      showError('Select a device');
      return;
    }
    if (!customId.trim()) {
      showError('Enter Custom ID');
      return;
    }
    const eventId = selectedDevice?.eventId?._id || selectedDevice?.eventId;
    if (!eventId) {
      showError('Device not associated with any event');
      return;
    }
    
    scanMutation.mutate({
      deviceId: selectedDeviceId,
      customId: customId.trim(),
      eventId,
      type,
    });
  }, [selectedDeviceId, customId, selectedDevice, scanMutation, showError]);

  // ✅ Loading state
  if (devicesLoading || scansLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const isLoading = scanMutation.isPending;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-[#2b3445]">Scanner Dashboard</h1>
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Device Selection & Scan Form */}
        <div className="bg-white rounded-xl border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6">
          <h2 className="text-lg font-semibold mb-4 text-[#2b3445]">Scan Entry/Exit</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Device</label>
            <select
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
              disabled={isLoading || devices.length === 0}
            >
              {devices.length === 0 ? (
                <option value="">No devices available</option>
              ) : (
                devices.map(device => (
                  <option key={device._id} value={device._id}>
                    {device.name} – {device.eventId?.name || 'Unknown event'}
                  </option>
                ))
              )}
            </select>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Attendee Custom ID</label>
            <input
              type="text"
              value={customId}
              onChange={(e) => setCustomId(e.target.value)}
              placeholder="Enter Custom ID"
              className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
              autoFocus
              disabled={isLoading}
            />
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={() => handleScan('entry')}
              disabled={isLoading || !selectedDeviceId || !customId.trim()}
              className="flex-1 bg-green-600 text-white py-2.5 rounded-[8px] font-medium hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isLoading ? 'Processing...' : 'Entry'}
            </button>
            <button
              onClick={() => handleScan('exit')}
              disabled={isLoading || !selectedDeviceId || !customId.trim()}
              className="flex-1 bg-red-600 text-white py-2.5 rounded-[8px] font-medium hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isLoading ? 'Processing...' : 'Exit'}
            </button>
          </div>
        </div>

        {/* Assigned Devices List */}
        <div className="bg-white rounded-xl border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6">
          <h2 className="text-lg font-semibold mb-4 text-[#2b3445]">My Assigned Devices</h2>
          {devices.length === 0 ? (
            <p className="text-gray-500">No devices assigned to you.</p>
          ) : (
            <ul className="space-y-2">
              {devices.map(device => (
                <li key={device._id} className="border-b border-[#dae1e7] pb-2 flex justify-between">
                  <span className="text-gray-700">{device.name}</span>
                  <span className="text-sm text-gray-500">{device.eventId?.name || 'Unknown'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent Scans */}
      <div className="mt-8 bg-white rounded-xl border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6">
        <h2 className="text-lg font-semibold mb-4 text-[#2b3445]">Recent Scans</h2>
        {recentScans.length === 0 ? (
          <p className="text-gray-500">No scans yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Custom ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentScans.map(scan => (
                  <tr key={scan._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {new Date(scan.entryTime).toLocaleTimeString()}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 font-mono">
                      {scan.customId}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {scan.name}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        scan.status === 'IN' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {scan.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScannerDashboard;